import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { GoogleMap, MarkerF, useLoadScript } from '@react-google-maps/api';
import PhoneNumberDisplay from '../common/components/PhoneNumberDisplay';
import CompanyDistanceChip from '../common/components/CompanyDistanceChip';
import { CompanyInfo } from './companyInfo/CompanyInfoCard';
import { CompanyServices } from '../common/models/user.models';

interface CompanyContactInfoBoxProps {
  company: Omit<CompanyInfo, keyof CompanyServices>;
}
export default function CompanyContactInfoBox({ company }: CompanyContactInfoBoxProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'contracts']);
  const { isLoaded } = useLoadScript({ googleMapsApiKey: import.meta.env.REACT_APP_GOOGLE_MAPS_API_KEY });

  return (
    <Box>
      <Typography sx={{ mb: 1.5 }} variant="titleMedium">
        {t('common:company')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            md: 'row',
          },
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mb: 1.5,
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                lg: 'row',
              },
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="bodySmall" color="secondary" sx={{ mb: 0.5 }}>{t('common:phone')}</Typography>
              <PhoneNumberDisplay
                phoneNumber={company.contactPhoneNumber}
                typographyVariant="bodyMedium"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="bodySmall" color="secondary" sx={{ mb: 0.5 }}>{t('common:email_address')}</Typography>
              <Typography variant="bodyMedium">{company.contactEmail}</Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="bodySmall" color="secondary" sx={{ mb: 0.5 }}>{t('common:address')}</Typography>
            <Typography variant="bodyMedium" sx={{ mb: 1 }}>{company.addresses[0].formattedAddress}</Typography>
            <CompanyDistanceChip targetGeoLoc={{ lat: company.addresses[0].latitude, lng: company.addresses[0].longitude }} />
          </Box>
        </Box>

        <Box
          sx={{
            width: {
              sm: '100%',
              md: 389,
            },
            height: 200,
          }}
        >
          {isLoaded && (
            <GoogleMap
              mapContainerStyle={{
                width: '100%',
                height: 200,
                borderRadius: 12,
              }}
              options={{
                disableDefaultUI: true,
              }}
              zoom={8}
              center={{
                lat: company.addresses[0].latitude,
                lng: company.addresses[0].longitude,
              }}
            >
              <MarkerF position={{ lng: company.addresses[0].longitude, lat: company.addresses[0].latitude }} clickable={false} />
            </GoogleMap>
          )}
        </Box>
      </Box>
    </Box>
  );
}
